/**
 * dataParser.ts - Data Structure Parser for Mappr
 * 
 * CHANGE HISTORY:
 * - 251126_1742 [pending] danielbeer: Add data structure parsing with Translatr
 * 
 * Parses source/target data structures to extract field definitions.
 * Supports JSON, CSV, TSV, XML via Translatr.
 * Detects array fields for multi-record generation.
 */

import type { Field, FieldType } from '../types/index';

/**
 * Parse result from data structure
 */
export interface ParseResult {
  fields: Field[];
  data: any;
  format: string;
  preview: any[];
  hasArrayFields: boolean;
  arrayFields: string[];
}

/**
 * Infer field type from value
 */
function inferFieldType(value: any): FieldType {
  if (value === null || value === undefined) return 'string';
  if (Array.isArray(value)) return 'array';
  if (typeof value === 'object') return 'object';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'boolean') return 'boolean';
  
  // Check for special string types
  const str = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return 'date';
  if (/^\d{4}-\d{2}-\d{2}T/.test(str)) return 'datetime';
  if (/^[\w.-]+@[\w.-]+\.\w+$/.test(str)) return 'email';
  if (/^[\d\s()+-]+$/.test(str) && str.length >= 10) return 'phone';
  if (/^https?:\/\//.test(str)) return 'url';
  
  return 'string';
}

/**
 * Parse data structure using Translatr
 * 
 * @param dataText - Raw data text (JSON, CSV, TSV, XML)
 * @param Translatr - Translatr instance from dependr
 * @returns Parsed fields and metadata
 */
export async function parseDataStructure(
  dataText: string,
  Translatr: any
): Promise<ParseResult> {
  // Use Translatr to auto-detect and parse
  const result = await Translatr.autoParse(dataText);
  
  let fields: Field[] = [];
  let preview: any[] = [];
  let arrayFields: string[] = [];
  let hasArrayFields = false;
  
  if (result.format === 'json') {
    if (Array.isArray(result.data)) {
      preview = result.data.slice(0, 5);
      if (result.data.length > 0) {
        const firstRecord = result.data[0];
        const fieldNames = Object.keys(firstRecord);
        
        fields = fieldNames.map((name, index) => {
          const value = firstRecord[name];
          const type = inferFieldType(value);
          const isArray = Array.isArray(value);
          
          if (isArray) {
            arrayFields.push(name);
            hasArrayFields = true;
          }
          
          return {
            id: `field_${index}`,
            name,
            type,
            required: false,
            sample: isArray ? JSON.stringify(value) : value
          };
        });
      }
    } else if (typeof result.data === 'object') {
      preview = [result.data];
      const fieldNames = Object.keys(result.data);
      
      fields = fieldNames.map((name, index) => {
        const value = result.data[name];
        const type = inferFieldType(value);
        const isArray = Array.isArray(value);
        
        if (isArray) {
          arrayFields.push(name);
          hasArrayFields = true;
        }
        
        return {
          id: `field_${index}`,
          name,
          type,
          required: false,
          sample: isArray ? JSON.stringify(value) : value
        };
      });
    }
  } else if (result.format === 'csv' || result.format === 'tsv') {
    if (Array.isArray(result.data) && result.data.length > 0) {
      preview = result.data.slice(0, 5);
      const firstRecord = result.data[0];
      const fieldNames = Object.keys(firstRecord);
      
      fields = fieldNames.map((name, index) => {
        const value = firstRecord[name];
        const type = inferFieldType(value);
        
        return {
          id: `field_${index}`,
          name,
          type,
          required: false,
          sample: value
        };
      });
    }
  }
  
  return {
    fields,
    data: result.data,
    format: result.format,
    preview,
    hasArrayFields,
    arrayFields
  };
}

/**
 * Generate fields from a schema object
 * 
 * @param schema - Schema object with field definitions
 * @returns Array of Field objects
 */
export function fieldsFromSchema(schema: Record<string, any>): Field[] {
  return Object.entries(schema).map(([name, config], index) => ({
    id: `field_${index}`,
    name,
    type: (config.type || 'string') as FieldType,
    required: config.required || false,
    description: config.description,
    sample: config.sample || config.default
  }));
}
